/**
 * The click-to-accept step every signed-in client meets (Lloyd, Jul 31: "a 'click to accept' button
 * on his portal that all clients will be confronted with before moving forward").
 *
 * Two consents, presented together but answered separately:
 *  - Terms of representation is required, so the portal stays blocked until it is accepted.
 *  - Sharing with a referral partner is optional. A client may decline and carry on using the
 *    portal normally; declining simply means their case cannot be shown to a partner. Bundling the
 *    two would make "consent" meaningless, because refusing would cost them access.
 *
 * Renders nothing at all when there is nothing outstanding, so it costs one request per session and
 * is invisible to a client who has already answered.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from '@mui/material';
import {
  ConsentRecipient,
  RECIPIENT_LABELS,
  RECIPIENT_ORDER,
  SHARING_CHANGE_NOTE,
  SHARING_INTRO,
  SHARING_PROMPT,
} from '../../constants/consentRecipients';

/** Twin of ConsentType in backend/src/config/consentDocuments.ts. Change both together. */
type ConsentType =
  | 'third-party-sharing'
  | 'terms-of-representation'
  | 'text-messaging';

// The recipient list is NOT declared here. It lived in this file and drifted from the server's; it
// now sits in constants/consentRecipients.ts with a test pinning the two sides together. Section 2
// asks the client to tick recipients individually rather than agree to a single yes, because one
// boolean cannot express "my payor but not my employer", which the form explicitly invites.

interface ConsentStatus {
  type: ConsentType;
  title: string;
  summary: string;
  /** The words on the decline button. Absent falls back to a neutral refusal. */
  declineSummary?: string | null;
  body: string;
  required: boolean;
  granted: boolean;
  outdated: boolean;
  /** True when the client must also elect WHO may be told, not merely yes or no. */
  electsRecipients?: boolean;
}

/** A live case of this client's. Section A names a cause number, so the client is shown which. */
interface ConsentMatter {
  ticketGuid: string;
  ticketNumber: string;
  causeNumber: string | null;
}

const ConsentGate: React.FC = () => {
  const [consents, setConsents] = useState<ConsentStatus[] | null>(null);
  const [matters, setMatters] = useState<ConsentMatter[]>([]);
  const [submitting, setSubmitting] = useState<ConsentType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<ConsentRecipient[]>([]);
  const [otherName, setOtherName] = useState('');
  const [otherRelationship, setOtherRelationship] = useState('');

  const load = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(`${process.env.API_HOST || ''}/api/v1/user/consents`, {
        credentials: 'include',
      });
      if (!response.ok) {
        // A client who isn't signed in has nothing to answer — stay silent rather than showing an
        // error over a page that is working perfectly well.
        setConsents([]);
        return;
      }
      const data = await response.json();
      setConsents(data.consents ?? []);
      setMatters(data.matters ?? []);
    } catch {
      setConsents([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRecipient = (recipient: ConsentRecipient): void => {
    setRecipients((current) =>
      current.includes(recipient)
        ? current.filter((r) => r !== recipient)
        : [...current, recipient],
    );
  };

  const answer = async (type: ConsentType, granted: boolean): Promise<void> => {
    setSubmitting(type);
    setError(null);
    // Only a granted sharing consent carries recipients. A refusal names nobody, and the other
    // consents have no recipients to name.
    const elects = granted && consents?.some((c) => c.type === type && c.electsRecipients);
    try {
      const response = await fetch(`${process.env.API_HOST || ''}/api/v1/user/consents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(
          elects
            ? {
                type,
                granted,
                // Every live case, because the question was asked once and the screen named them
                // all. Sharing is recorded per matter, so this writes one answer per case.
                ticket_guids: matters.map((m) => m.ticketGuid),
                recipients,
                otherName: recipients.includes('other') ? otherName.trim() || null : null,
                otherRelationship: recipients.includes('other')
                  ? otherRelationship.trim() || null
                  : null,
              }
            : { type, granted },
        ),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Could not save your answer');
      setConsents(data.consents ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your answer');
    } finally {
      setSubmitting(null);
    }
  };

  if (!consents) return null;

  // Anything unanswered, or answered against wording that has since changed.
  const outstanding = consents.filter((c) => !c.granted);
  if (outstanding.length === 0) return null;

  const blocking = outstanding.some((c) => c.required);

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      // A required consent cannot be dismissed; an optional one can be left for later.
      disableEscapeKeyDown={blocking}
      onClose={() => {
        if (!blocking) setConsents([]);
      }}
    >
      <DialogTitle>Before you continue</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {outstanding.map((consent, index) => (
          <Box key={consent.type} sx={{ mb: 3 }}>
            {index > 0 && <Divider sx={{ mb: 3 }} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {consent.title}
            </Typography>
            {consent.outdated && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                We have updated this since you last agreed, so please read it again.
              </Typography>
            )}
            {/*
              `pre-line` because the body is assembled as separate lines joined with "\n"
              (consentDocuments.ts) and HTML collapses those, so every bullet and numbered item ran
              together into one paragraph. Never seen by a client, because this screen has never been
              switched on. `pre-line` keeps the line breaks and still wraps normally.
            */}
            <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
              {consent.body}
            </Typography>

            {consent.electsRecipients && (
              <Box sx={{ mt: 2 }}>
                {matters.length > 0 && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Your answer applies to{' '}
                    {matters.length === 1 ? 'this case' : 'these cases'}:{' '}
                    <strong>
                      {matters
                        .map((m) => m.causeNumber || m.ticketNumber)
                        .join(', ')}
                    </strong>
                    .
                  </Typography>
                )}
                {/* His standing note and prompt, from Section 2 of the acceptance screen he drafted
                    on 16 Sep 2026. The heading above the boxes is his sentence, not a summary. */}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {SHARING_INTRO}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {SHARING_CHANGE_NOTE}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {SHARING_PROMPT}
                </Typography>
                <FormGroup>
                  {RECIPIENT_ORDER.map((recipient) => (
                    <FormControlLabel
                      key={recipient}
                      control={
                        <Checkbox
                          checked={recipients.includes(recipient)}
                          onChange={() => toggleRecipient(recipient)}
                          disabled={submitting !== null}
                        />
                      }
                      label={RECIPIENT_LABELS[recipient]}
                    />
                  ))}
                </FormGroup>
                {recipients.includes('other') && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    <TextField
                      label="Their name"
                      size="small"
                      value={otherName}
                      onChange={(e) => setOtherName(e.target.value)}
                      disabled={submitting !== null}
                    />
                    <TextField
                      label="Who they are to you"
                      size="small"
                      value={otherRelationship}
                      onChange={(e) => setOtherRelationship(e.target.value)}
                      disabled={submitting !== null}
                    />
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                // A grant that names nobody would read as permission while sharing with no one, and
                // the partner link only checks that a grant exists.
                disabled={submitting !== null || (consent.electsRecipients && recipients.length === 0)}
                onClick={() => answer(consent.type, true)}
              >
                {submitting === consent.type ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  consent.summary
                )}
              </Button>
              {!consent.required && (
                <Button
                  variant="text"
                  disabled={submitting !== null}
                  onClick={() => answer(consent.type, false)}
                >
                  {consent.declineSummary || 'No, not this time'}
                </Button>
              )}
            </Box>
          </Box>
        ))}
      </DialogContent>
      {!blocking && (
        <DialogActions>
          <Button onClick={() => setConsents([])}>Decide later</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ConsentGate;
